import React from 'react';
import { Container, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Box } from '@mui/material';
import {MyServices} from "../constants";
import {convertHexToRgba} from "../App/utils";
import {colorText, intensePaper} from "../App/theme";


const formatContextSize = (size) => `${Math.round(size / 1000)}K`;

const formatPrice = (price, n = 5) => `$${(price / (100*1000)).toFixed(n)}`;

const precision = (model) => {
    if (model?.size === 'Large') return 3
    if (model?.size === 'Medium') return 4
    return 5
}

export const renderServicePrices = (model) => {
    const priceTypes = {
        inputPrice: { label: ' / 1K input tokens', precision: precision(model) },
        outputPrice: { label: ' / 1K output tokens', precision: precision(model) },
        visionPricePerImage: { label: ' / image', precision: precision(model) },
        pricePerRequest: { label: ' / request', precision: 4 },
        pricePerMinute: { label: ' / minute', precision: 4 },
        pricePerCharacter: { label: ' / character', precision: 5 },
    };

    const formattedPrices = Object.entries(priceTypes).map(([key, { label, precision }]) => {
        if (model[key] !== undefined) {
            return `${formatPrice(model[key], precision)}${label}`;
        }
        return null;
    }).filter(price => price !== null);

    return formattedPrices.join(', ');
};

const style = { p: 3, paddingBottom: 1, paddingTop: 2 , mb: 2, textAlign: 'left'  }
const style2 = { p: 3, paddingBottom: 1, paddingTop: 2 , mb: 2, textAlign: 'left', backgroundColor: convertHexToRgba(intensePaper, 0.0) }
const baseStyle = {marginLeft: '-30px', marginRight: '-30px'};

const ChatModelsTable = ({ chatModels, embeddingModels }) => {
    // Sort chat models by vendor and then by output price
    const sortedModels = Object.entries(chatModels || embeddingModels).sort((a, b) => {
        // Define the order of vendors explicitly
        const vendorOrder = ['openai', 'anthropic', 'mistral'];

        // Extract vendors/subvendors
        let vendorA = a[1].subvendor || a[1].vendor;
        let vendorB = b[1].subvendor || b[1].vendor;

        // Get the order index from the vendorOrder array
        let indexA = vendorOrder.indexOf(vendorA);
        let indexB = vendorOrder.indexOf(vendorB);

        // If either vendor is not found in the vendorOrder array, set them to the end of the sort order
        indexA = indexA === -1 ? vendorOrder.length : indexA;
        indexB = indexB === -1 ? vendorOrder.length : indexB;

        // Compare by vendor order first
        if (indexA < indexB) return -1;
        if (indexA > indexB) return 1;

        // If vendors are the same, then sort by outputPrice
        return b[1].outputPrice - a[1].outputPrice;
    });

    return (
        <Box style={{marginBottom: '50px', ...baseStyle}} >
            <Paper elevation={0} sx={style2}>
                <Typography variant="h6" gutterBottom>
                    {chatModels
                        ? 'Chat Models'
                        : 'Embedding Models'}
                </Typography>
                <Typography paragraph>
                    {chatModels
                        ? 'AI chat models are priced based on the usage of input and output tokens, calculated per 1000 tokens. A token can be a word, part of a word, or punctuation, serving as the basic unit the model processes. Input tokens are the text prompts given to the model, while output tokens are the responses generated by the model'
                        : 'Embeddings models are priced based on the usage of input tokens, calculated per 1000 tokens. A token can be a word, part of a word, or punctuation, serving as the basic unit the model processes. Input tokens are the text data provided to the model for generating embeddings.'}
                </Typography>
            </Paper>
            <TableContainer component={Paper}>
                <Table aria-label="chat models pricing table">
                    <TableHead>
                        <TableRow>
                            <TableCell>Service Name</TableCell>
                            <TableCell>Vendor</TableCell>
                            <TableCell>Text Input</TableCell>
                            {chatModels && <TableCell>Text Output</TableCell>}
                            {chatModels && <TableCell>Image Input</TableCell>}
                            {/*<TableCell>Description</TableCell>*/}
                            <TableCell>Context Size</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sortedModels.map(([key, model]) => (
                            <TableRow key={key}>
                                <TableCell>{model.name}</TableCell>
                                <TableCell>{model.vendor === 'bedrock' ? key.split('.')[0] : model.vendor}</TableCell>
                                <TableCell>{formatPrice(model.inputPrice, precision(model))} / 1K tokens</TableCell>
                                {chatModels && <TableCell>{formatPrice(model.outputPrice, precision(model))} / 1K tokens</TableCell>}
                                {chatModels && <TableCell>{model.visionPricePerImage ? formatPrice(model.visionPricePerImage, precision(model)) + ' / image' : ''} </TableCell>}
                                {/*<TableCell>{model.serviceDescription || 'No description available'}</TableCell>*/}
                                <TableCell>{formatContextSize(model.context)}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

const VisualModelsTable = ({ visualModels }) => {
    return (
        <Box style={{marginBottom: '50px', ...baseStyle}} >
            <Paper elevation={0} sx={style2}>
                <Typography variant="h6" gutterBottom>
                    Visual Models
                </Typography>
                <Typography paragraph>
                    Visual models, which convert text descriptions into images, are priced on a per-request basis. These models leverage advanced algorithms to interpret textual prompts and generate corresponding visual content. Pricing is standardized per request, ensuring straightforward and consistent pricing.
                </Typography>
            </Paper>
            <TableContainer component={Paper}>
                <Table aria-label="visual models pricing table">
                    <TableHead>
                        <TableRow>
                            <TableCell>Service Name</TableCell>
                            {/*<TableCell>Interfaces</TableCell>*/}
                            <TableCell>Price</TableCell>
                            <TableCell>Description</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {Object.entries(visualModels).map(([key, model]) => (
                            <TableRow key={key}>
                                <TableCell>{model.name}</TableCell>
                                {/*<TableCell>{model.interfaces.join(', ')}</TableCell>*/}
                                <TableCell>{formatPrice(model.pricePerRequest, 3)} / request</TableCell>
                                <TableCell>{model.serviceDescription || 'No description available'}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

const WebToolsTable = ({ webTools }) => {
    return (
        <Box style={{marginBottom: '50px', ...baseStyle}} >
            <Paper elevation={0} sx={style2}>
                <Typography variant="h6" gutterBottom>
                    Web Tools
                </Typography>
                <Typography paragraph>
                    Web tools encompass a range of services designed to interact with web content, including search engines, webpage readers, and more. These tools are priced based on the complexity and the computational resources required for each request.
                </Typography>
            </Paper>
            <TableContainer component={Paper}>
                <Table aria-label="web tools pricing table">
                    <TableHead>
                        <TableRow>
                            <TableCell>Service Name</TableCell>
                            <TableCell>Description</TableCell>
                            <TableCell>Price</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {Object.entries(webTools).map(([key, model]) => (
                            <TableRow key={key}>
                                <TableCell>{model.name}</TableCell>
                                <TableCell>{model.serviceDescription || 'No description available'}</TableCell>
                                {model.pricePerRequest && <TableCell>{formatPrice(model.pricePerRequest, 4)} / request</TableCell>}
                                {model.pricePerMinute && <TableCell>{formatPrice(model.pricePerMinute, 4)} / minute</TableCell>}
                                {model.pricePerCharacter && <TableCell>{formatPrice(model.pricePerCharacter, 5)} / character</TableCell>}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

const AudioModelsTable = ({ audioModels }) => {
    return (
        <Box style={{marginBottom: '50px', ...baseStyle}} >
            <Paper elevation={0} sx={style2}>
                <Typography variant="h6" gutterBottom>
                    Audio Models
                </Typography>
                <Typography paragraph>
                    Audio models offer capabilities ranging from speech-to-text conversion to generating synthetic speech. Pricing varies based on the model's complexity and the length of the audio processed.
                </Typography>
            </Paper>
            <TableContainer component={Paper}>
                <Table aria-label="audio models pricing table">
                    <TableHead>
                        <TableRow>
                            <TableCell>Service Name</TableCell>
                            <TableCell>Description</TableCell>
                            <TableCell>Price</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {Object.entries(audioModels).map(([key, model]) => (
                            <TableRow key={key}>
                                <TableCell>{model.name}</TableCell>
                                <TableCell>{model.serviceDescription || 'No description available'}</TableCell>
                                <TableCell>
                                    {model.pricePerRequest && <>{formatPrice(model.pricePerRequest, 4)} / request</>}
                                    {model.pricePerMinute && <>{formatPrice(model.pricePerMinute, 4)} / minute</>}
                                    {model.pricePerCharacter && <>{formatPrice(model.pricePerCharacter, 5)} / character</>}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

const DatabaseServicesTable = ({ databaseServices }) => {
    return (
        <Box style={{ marginBottom: '50px', ...baseStyle }} >
            <Paper elevation={0} sx={style2}>
                <Typography variant="h6" gutterBottom>
                    Serverless NoSQL Database
                </Typography>
                <Typography paragraph>
                    The serverless NoSQL database service is fully managed, scalable, and high-performance. Pricing is based on data storage and read/write operations. Read costs are measured in Read Units (RUs), with one RU covering items up to 4 KB. Write costs are measured in Write Units (WUs), with one WU covering items up to 1 KB.
                </Typography>
            </Paper>
            <TableContainer component={Paper}>
                <Table aria-label="database services pricing table">
                    <TableHead>
                        <TableRow>
                            <TableCell>Service Name</TableCell>
                            <TableCell>Read</TableCell>
                            <TableCell>Write</TableCell>
                            <TableCell>Storage</TableCell>
                            <TableCell>Description</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {Object.entries(databaseServices).map(([key, model]) => (
                            <TableRow key={key}>
                                <TableCell>{model.name}</TableCell>
                                <TableCell>{formatPrice(model.readPrice * 1000, 2)} / 1M Read Units</TableCell>
                                <TableCell>{formatPrice(model.writePrice * 1000, 2)} / 1M Write Units</TableCell>
                                <TableCell>{formatPrice(model.storagePrice * 1000, 2)} / GB/month</TableCell>
                                <TableCell>{model.serviceDescription || 'No description available'}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};



export const Pricing = () => {
    // Filter services by category
    const chatModels = Object.fromEntries(Object.entries(MyServices).filter(([key, value]) => value.category === 'Chat'));
    const visualModels = Object.fromEntries(Object.entries(MyServices).filter(([key, value]) => value.category === 'Visual'));
    const webTools = Object.fromEntries(Object.entries(MyServices).filter(([key, value]) => value.category === 'Web'));
    const audioModels = Object.fromEntries(Object.entries(MyServices).filter(([key, value]) => value.category === 'Audio'));
    const embeddingModels = Object.fromEntries(Object.entries(MyServices).filter(([key, value]) => value.category === 'Embedding'));
    const databaseServices = Object.fromEntries(Object.entries(MyServices).filter(([key, value]) => value.category === 'Database'));

    return (

        <Container sx={{ mt: 4 }} style={{ color: colorText, textAlign: 'left', marginTop: '-10px' }} >
            <Paper elevation={0} sx={style} style={baseStyle} >
                <Typography variant="h6" gutterBottom>
                    Pricing Overview
                </Typography>
                <Typography paragraph>
                    At OpenKBS, customers pay only for the resources they use, ensuring cost efficiency across a diverse range of services. This model is designed to support scalability, allowing you to adjust your usage based on current needs without facing unexpected fees or charges. For every transaction on our platform, there is a nominal fee of 1 credit, equivalent to exactly $0.00001. This micro-transaction fee is part of our commitment to maintaining a transparent and fair pricing structure, ensuring that you are billed precisely for the resources consumed.
                </Typography>
            </Paper>

            <div style={{ height: '30px' }}></div>
            <DatabaseServicesTable databaseServices={databaseServices} />
            <div style={{ height: '30px' }}></div>
            <ChatModelsTable chatModels={chatModels} />
            <div style={{ height: '30px' }}></div>
            <ChatModelsTable embeddingModels={embeddingModels} />
            <div style={{ height: '30px' }}></div>
            <VisualModelsTable visualModels={visualModels} />
            <div style={{ height: '30px' }}></div>
            <WebToolsTable webTools={webTools} />
            <div style={{ height: '30px' }}></div>
            <AudioModelsTable audioModels={audioModels} />

        </Container>

    );
};